import { AIPlanCampaignBuilder, AIPlanCampaignBuilderType } from 'api/interfaces/AIPlan'
import moment from 'moment'

const channelTypeSortOrder: Record<string, number> = {
  [AIPlanCampaignBuilderType.TV]: 0,
  [AIPlanCampaignBuilderType.Billboard]: 1,
  [AIPlanCampaignBuilderType.Audio]: 2,
  [AIPlanCampaignBuilderType.Internet]: 3,
  [AIPlanCampaignBuilderType.OffAdCritter]: 4
}

export const sortAIPlanCampaigns = (campaigns: AIPlanCampaignBuilder[]): AIPlanCampaignBuilder[] => {
  return [...campaigns].sort((a, b) => {
    const startDateDiff = moment(a.startDate).valueOf() - moment(b.startDate).valueOf()
    if (startDateDiff !== 0) return startDateDiff

    const aTypeOrder = channelTypeSortOrder[a.campaignType] ?? 999
    const bTypeOrder = channelTypeSortOrder[b.campaignType] ?? 999
    if (aTypeOrder !== bTypeOrder) return aTypeOrder - bTypeOrder

    return (a.name ?? '').localeCompare(b.name ?? '')
  })
}

const getEarliestStartDate = (campaigns: AIPlanCampaignBuilder[]) => {
  const sorted = [...campaigns].sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
  return sorted.length > 0 ? moment(sorted[0].startDate) : undefined
}

const getLatestEndDate = (campaigns: AIPlanCampaignBuilder[]) => {
  const withEndDates = campaigns
    .map(c => {
      if (c.endDate) {
        return moment(c.endDate)
      }
      if (c.intervalUnit && c.intervalFrequency && c.intervalDuration) {
        let momentUnit: moment.unitOfTime.DurationConstructor = 'month'
        if (c.intervalUnit === 'Day') momentUnit = 'day'
        else if (c.intervalUnit === 'Week') momentUnit = 'week'
        return moment(c.startDate).add(c.intervalDuration * c.intervalFrequency, momentUnit)
      }
      return undefined
    })
    .filter((d): d is moment.Moment => d !== undefined)

  if (withEndDates.length === 0) return undefined
  return withEndDates.sort((a, b) => b.valueOf() - a.valueOf())[0]
}

const getAIPlanTotalMonthRange = (campaigns: AIPlanCampaignBuilder[]): number => {
  if (!campaigns || campaigns.length === 0) return 0
  const startDate = getEarliestStartDate(campaigns)
  const endDate = getLatestEndDate(campaigns)
  if (!startDate || !endDate) return 0
  return endDate.endOf('month').diff(startDate.endOf('month'), 'months')
}

const getAIPlanAverageMonthlyBudget = (durationInMonths: number, campaigns: AIPlanCampaignBuilder[]): number => {
  if (!campaigns || campaigns.length === 0 || durationInMonths === 0) return 0
  const totalBudget = campaigns.reduce((total, builder) => {
    return total + (builder.intervalBudget * (builder.intervalDuration || 1))
  }, 0)
  return totalBudget / durationInMonths
}

export const getAIPlanDescription = (accountName: string) => {
  const firstParagraph = `This comprehensive marketing plan for ${accountName} has been meticulously crafted to maximize reach and engagement across your target demographic. Our strategic approach combines in-depth market research, competitive analysis, and consumer behavior insights to create a data-driven foundation for your advertising campaigns. The plan includes detailed audience segmentation, optimal budget allocation recommendations, and precise targeting strategies designed to deliver measurable results and strong return on investment.`

  const secondParagraph = `Each campaign component has been carefully selected and optimized based on your specific business objectives, geographic markets, and customer personas. The implementation timeline includes detailed milestone tracking, performance metrics, and adaptive strategies to ensure campaign success throughout the entire execution period. This holistic approach ensures maximum market penetration while maintaining cost efficiency and brand consistency across all touchpoints.`

  return {
    firstParagraph,
    secondParagraph,
    combined: `${firstParagraph}\n\n${secondParagraph}`
  }
}

const offAdCritterChannelDescriptions: Record<string, string> = {
  Google: 'Google Search Ads target the bottom of the funnel, where user intent is at its highest. By bidding on keywords closely aligned with your product or service, these ads capture prospects actively searching for solutions - delivering highly qualified traffic with strong conversion potential.',
  Facebook: 'Facebook ads serve the middle of the funnel, driving engagement and interaction with your brand. Campaigns leverage precise demographic and geographic targeting aligned with your market research and personas. Formats like lead forms, event registrations, and content downloads encourage meaningful action from your audience.',
  Instagram: 'Instagram ads operate in the upper funnel, maintaining brand presence and awareness through visually-driven content. Feed placements and Stories keep your brand visible to your target audience, working effectively as part of a multi-channel effort to reinforce messaging delivered through other formats.',
  LinkedIn: 'LinkedIn ads target the middle-to-lower funnel, excelling at B2B engagement and lead generation. Precise targeting by industry, job title, and professional attributes makes it uniquely effective for reaching decision-makers. LeadGen forms and sponsored content are standout formats for driving qualified business leads.',
  TikTok: 'TikTok ads maximize upper-funnel reach, particularly effective when the target audience skews under 35. The platform thrives with products that can be physically demonstrated, delivering high-volume awareness at a competitive cost per impression.'
}

const offAdCritterPlatformOrder = ['Google', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok']

const tvParagraph = 'CTV is particularly effective at both communicating your entire message and raising the credibility level of your brand. While it is more expensive than the other formats on a CPM basis, it will be tightly targeted to your intended audience for maximum impact.'

const audioParagraph = 'Streaming Audio ads reach listeners through streaming music and digital radio, maintaining brand presence in moments when screen-based advertising cannot. Like CTV, Streaming Audio is tightly targeted to your intended audience and complements visual channels by capturing attention during commutes, workouts, and focus time.'

const internetWithTvParagraph = 'Internet ads are less credibility-building than CTV, but they are both highly targetable and low cost, making them ideal for supporting your CTV campaign and keeping your brand top-of-mind. We can use these internet ads for website retargeting (those who have visited your website) and CTV retargeting (those who have seen your CTV commercial). Optionally, we can also target these ads to any custom audience or first-party data list you provide.'

const internetStandaloneParagraph = 'Internet ads are both highly targetable and low cost, making them ideal for keeping your brand top-of-mind. We can use these internet ads for website retargeting (those who have visited your website) and CTV retargeting (those who have seen your CTV commercial). Optionally, we can also target these ads to any custom audience or first-party data list you provide.'

const billboardWithTvAndInternetParagraph = 'Billboards lack the targeting controls of CTV and Internet, but they add far more reach. This makes them incredibly effective at supporting brand awareness across an entire geographic area. Here we will use them to keep you top-of-mind for those who have seen your targeted advertising and to build awareness for those who have not.'

const billboardWithTvParagraph = 'Billboards lack the targeting controls of CTV, but they add far more reach. This makes them incredibly effective at supporting brand awareness across an entire geographic area. Here we will use them to keep you top-of-mind for those who have seen your CTV advertising and to build awareness for those who have not.'

const billboardWithInternetParagraph = 'Billboards lack the targeting controls of internet ads, but they add far more reach. This makes them incredibly effective at building credibility and supporting brand awareness across an entire geographic area. Here we will use them to keep you top-of-mind for those who have seen your targeted advertising and to build awareness for those who have not.'

const joinChannelNames = (names: string[]): string => {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

export const getAIPlanChannelUtilizationContent = (campaigns: AIPlanCampaignBuilder[]): string | null => {
  const usedTypes = new Set(campaigns.map(campaign => campaign.campaignType))
  const hasTV = usedTypes.has(AIPlanCampaignBuilderType.TV)
  const hasAudio = usedTypes.has(AIPlanCampaignBuilderType.Audio)
  const hasInternet = usedTypes.has(AIPlanCampaignBuilderType.Internet)
  const hasBillboard = usedTypes.has(AIPlanCampaignBuilderType.Billboard)

  const usedOffAdCritterNames = new Set(
    campaigns
      .filter(c => c.isOffAdCritter && c.offAdCritterName)
      .map(c => c.offAdCritterName)
  )

  const adCritterChannelNames: string[] = []
  if (hasTV) adCritterChannelNames.push('Connected TV (CTV)')
  if (hasAudio) adCritterChannelNames.push('Streaming Audio')
  if (hasInternet) adCritterChannelNames.push('Programmatic Internet Ads')
  if (hasBillboard) adCritterChannelNames.push('Digital Roadside Billboards')

  const offAdCritterParagraphs = offAdCritterPlatformOrder
    .filter(name => usedOffAdCritterNames.has(name) && offAdCritterChannelDescriptions[name])
    .map(name => offAdCritterChannelDescriptions[name])

  const totalChannelCount = adCritterChannelNames.length + offAdCritterParagraphs.length

  if (totalChannelCount === 0) {
    return null
  }

  const paragraphs: string[] = []

  if (adCritterChannelNames.length >= 3) {
    paragraphs.push(`Seeing related ads across different channel formats significantly increases their memorability. In fact, Nielsen's CrossPlatform Advertising Report indicates that advertising using three or more integrated channels results in a 35% higher ROI than single-channel efforts. As such, this plan uses ${joinChannelNames(adCritterChannelNames)}.`)
  } else if (adCritterChannelNames.length === 2) {
    paragraphs.push(`This plan uses ${joinChannelNames(adCritterChannelNames)}.`)
  }

  if (hasTV) {
    paragraphs.push(tvParagraph)
  }

  if (hasAudio) {
    paragraphs.push(audioParagraph)
  }

  if (hasInternet) {
    paragraphs.push(hasTV ? internetWithTvParagraph : internetStandaloneParagraph)
  }

  paragraphs.push(...offAdCritterParagraphs)

  if (hasBillboard) {
    if (hasTV && hasInternet) {
      paragraphs.push(billboardWithTvAndInternetParagraph)
    } else if (hasTV) {
      paragraphs.push(billboardWithTvParagraph)
    } else {
      paragraphs.push(billboardWithInternetParagraph)
    }
  }

  return paragraphs.join('\n\n')
}

export const getAIPlanBudgetContent = (campaigns: AIPlanCampaignBuilder[], useMediaPlanWording: boolean = false): string => {
  const durationMonths = getAIPlanTotalMonthRange(campaigns)
  const averageMonthlySpend = Math.round(getAIPlanAverageMonthlyBudget(durationMonths, campaigns))

  interface ChannelKey {
    type: AIPlanCampaignBuilderType
    offAdCritterName?: string
  }

  const isValidCampaignType = (campaign: AIPlanCampaignBuilder): boolean => {
    if (campaign.isOffAdCritter && campaign.offAdCritterName) {
      return true
    }
    return campaign.campaignType === AIPlanCampaignBuilderType.TV || campaign.campaignType === AIPlanCampaignBuilderType.Audio || campaign.campaignType === AIPlanCampaignBuilderType.Internet || campaign.campaignType === AIPlanCampaignBuilderType.Billboard
  }

  const channelBudgets = new Map<string, { key: ChannelKey; budget: number }>()
  let totalBudget = 0

  campaigns.forEach(campaign => {
    if (!isValidCampaignType(campaign)) return

    const channelId = campaign.isOffAdCritter && campaign.offAdCritterName ? `offadcritter_${campaign.offAdCritterName}` : campaign.campaignType

    const existing = channelBudgets.get(channelId)
    if (existing) {
      existing.budget += campaign.intervalBudget
    } else {
      channelBudgets.set(channelId, {
        key: {
          type: campaign.campaignType,
          offAdCritterName: campaign.offAdCritterName
        },
        budget: campaign.intervalBudget
      })
    }
    totalBudget += campaign.intervalBudget
  })

  const channelPercentages = new Map<string, number>()
  if (totalBudget > 0) {
    channelBudgets.forEach((value, channelId) => {
      const percentage = (value.budget / totalBudget) * 100
      channelPercentages.set(channelId, percentage)
    })
  }

  const formatPercentage = (percentage: number): string => {
    const rounded = Math.round(percentage)
    return rounded < 1 ? '<1' : rounded.toString()
  }

  const socialOrder = ['Google', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok']
  const customOffAdCritterOrder: string[] = []

  campaigns.forEach(campaign => {
    if (campaign.isOffAdCritter && campaign.offAdCritterName) {
      if (!socialOrder.includes(campaign.offAdCritterName) && !customOffAdCritterOrder.includes(campaign.offAdCritterName)) {
        customOffAdCritterOrder.push(campaign.offAdCritterName)
      }
    }
  })

  const usedChannels = Array.from(channelBudgets.entries())
    .sort(([keyA, valueA], [keyB, valueB]) => {
      const getOrderWeight = (_channelId: string, key: ChannelKey): number => {
        if (key.type === AIPlanCampaignBuilderType.TV) return 0
        if (key.type === AIPlanCampaignBuilderType.Billboard) return 1
        if (key.type === AIPlanCampaignBuilderType.Audio) return 2
        if (key.type === AIPlanCampaignBuilderType.Internet) return 3

        if (key.offAdCritterName) {
          const socialIndex = socialOrder.indexOf(key.offAdCritterName)
          if (socialIndex !== -1) return 4 + socialIndex
        }

        if (key.offAdCritterName) {
          const customIndex = customOffAdCritterOrder.indexOf(key.offAdCritterName)
          if (customIndex !== -1) return 20 + customIndex
        }

        return 1000
      }

      return getOrderWeight(keyA, valueA.key) - getOrderWeight(keyB, valueB.key)
    })
    .map(([channelId]) => channelId)

  const getChannelDisplayName = (_channelId: string, key: ChannelKey): string | null => {
    if (key.offAdCritterName) return key.offAdCritterName

    switch (key.type) {
      case AIPlanCampaignBuilderType.TV:
        return 'CTV'
      case AIPlanCampaignBuilderType.Audio:
        return 'streaming audio ads'
      case AIPlanCampaignBuilderType.Internet:
        return 'internet ads'
      case AIPlanCampaignBuilderType.Billboard:
        return 'digital billboards'
      default:
        return null
    }
  }

  let firstSentence = ''
  let secondSentence = ''
  let thirdSentence = ''

  const planType = useMediaPlanWording ? 'media plan' : 'marketing plan'

  if (durationMonths < 2) {
    secondSentence = `The monthly budget is $${averageMonthlySpend.toLocaleString()} per month.`
  } else {
    firstSentence = `This ${planType} spans ${durationMonths} month${durationMonths === 1 ? '' : 's'}. `
    secondSentence = `While the exact amount varies, the average monthly spend across this time period is $${averageMonthlySpend.toLocaleString()} per month.`
  }

  if (usedChannels.length > 1) {
    const validChannels = usedChannels.filter(channelId => {
      const channelData = channelBudgets.get(channelId)
      return channelData && getChannelDisplayName(channelId, channelData.key) !== null
    })

    if (validChannels.length === 0) {
      return `${firstSentence}${secondSentence}`
    }

    if (validChannels.length === 1) {
      const channelId = validChannels[0]
      const channelData = channelBudgets.get(channelId)!
      const displayName = getChannelDisplayName(channelId, channelData.key)
      thirdSentence = ` The budget is allocated to ${displayName}.`
    } else if (validChannels.length === 2) {
      const [firstChannelId, secondChannelId] = validChannels
      const firstChannelData = channelBudgets.get(firstChannelId)!
      const secondChannelData = channelBudgets.get(secondChannelId)!
      const firstName = getChannelDisplayName(firstChannelId, firstChannelData.key)
      const secondName = getChannelDisplayName(secondChannelId, secondChannelData.key)
      const firstPercentage = formatPercentage(channelPercentages.get(firstChannelId) || 0)
      const secondPercentage = formatPercentage(channelPercentages.get(secondChannelId) || 0)
      thirdSentence = ` Approximately ${firstPercentage}% of the budget is allocated to ${firstName} and ${secondPercentage}% to ${secondName}.`
    } else {
      const [firstChannelId, ...restChannelIds] = validChannels
      const firstChannelData = channelBudgets.get(firstChannelId)!
      const firstName = getChannelDisplayName(firstChannelId, firstChannelData.key)
      const firstPercentage = formatPercentage(channelPercentages.get(firstChannelId) || 0)

      const restStrings = restChannelIds.slice(0, -1).map(channelId => {
        const channelData = channelBudgets.get(channelId)!
        const displayName = getChannelDisplayName(channelId, channelData.key)
        const percentage = formatPercentage(channelPercentages.get(channelId) || 0)
        return `${percentage}% to ${displayName}`
      })

      const lastChannelId = restChannelIds[restChannelIds.length - 1]
      const lastChannelData = channelBudgets.get(lastChannelId)!
      const lastName = getChannelDisplayName(lastChannelId, lastChannelData.key)
      const lastPercentage = formatPercentage(channelPercentages.get(lastChannelId) || 0)

      thirdSentence = ` Approximately ${firstPercentage}% of the budget is allocated to ${firstName}, ${restStrings.join(', ')} and ${lastPercentage}% to ${lastName}.`
    }
  }

  return `${firstSentence}${secondSentence}${thirdSentence}`
}
